import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Filter } from 'lucide-react';
import Toggle from './Toggle';
import CustomSelect from './CustomSelect';

/**
 * FilterDropdown - A dropdown button containing multiple filter controls
 * Matches the pattern used in Bills.js
 */
function FilterDropdown({ filters = [], activeCount = 0, onClearAll }) {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Track window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleClearAll = () => {
    filters.forEach(filter => {
      if (filter.type === 'toggle') {
        filter.onChange(false);
      } else if (filter.type === 'select') {
        filter.onChange(filter.options?.[0]?.value || 'all');
      }
    });
    onClearAll?.();
  };

  // Calculate dropdown position
  const getDropdownStyle = () => {
    if (isMobile) {
      // On mobile, position at bottom of screen
      return {
        position: 'fixed',
        left: '12px',
        right: '12px',
        bottom: '12px',
        top: 'auto',
        zIndex: 9999
      };
    }
    
    // On desktop, position below button
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      return {
        position: 'fixed',
        top: rect.bottom + 8,
        left: rect.left,
        zIndex: 9999
      };
    }
    
    return { zIndex: 9999 };
  };

  // Close the dropdown - only called explicitly
  const closeDropdown = () => {
    setIsOpen(false);
  };

  return (
    <div className="filter-dropdown-container">
      <button 
        ref={buttonRef}
        className={`btn-filter ${activeCount > 0 ? 'has-filters' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <Filter size={18} />
        <span className="filter-btn-text">Filters</span>
        {activeCount > 0 && (
          <span className="filter-count">{activeCount}</span>
        )}
      </button>
      
      {isOpen && createPortal(
        <>
          <div 
            className="filter-dropdown-backdrop" 
            style={{ zIndex: 9998 }}
            onMouseDown={(e) => {
              // Only close if clicking directly on backdrop
              if (e.target === e.currentTarget) {
                closeDropdown();
              }
            }} 
          />
          <div 
            ref={dropdownRef}
            className="filter-dropdown"
            style={getDropdownStyle()}
          >
            <div className="filter-dropdown-header">
              <span>Filters</span>
              {activeCount > 0 && (
                <button className="btn-text-sm" onClick={handleClearAll}>
                  Clear all
                </button>
              )}
            </div>
            
            <div className="filter-dropdown-content">
              {filters.map(filter => (
                <div key={filter.id} className={`filter-item ${filter.type === 'toggle' ? 'filter-toggle' : ''}`}>
                  {filter.type === 'select' && (
                    <>
                      <label>{filter.label}</label>
                      <CustomSelect
                        value={filter.value}
                        onChange={(e) => filter.onChange(e.target.value)}
                        options={filter.options}
                        placeholder=""
                      />
                    </>
                  )}
                  {filter.type === 'toggle' && (
                    <>
                      <span>{filter.label}</span>
                      <Toggle
                        checked={filter.value}
                        onChange={filter.onChange}
                      />
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        </>,
        document.body
      )}
    </div>
  );
}

export default FilterDropdown;
